import { memo, useCallback, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useOptimizedCarousel } from "@/hooks/useOptimizedCarousel";
import WebPImage from "@/components/common/WebPImage";

interface GalleryImage {
  id: number;
  src: string;
  alt: string;
  title: string;
  description: string;
}

interface PerformantCarouselProps {
  images: GalleryImage[];
  title: string;
  packageDescription: string;
  carType: string;
}

const PerformantCarousel = memo(({ 
  images, 
  title, 
  packageDescription, 
  carType 
}: PerformantCarouselProps) => {
  const { currentIndex, nextSlide, prevSlide } = useOptimizedCarousel({ 
    itemsLength: images?.length || 0 
  });
  
  const [imageLoaded, setImageLoaded] = useState(true);

  const currentImage = useMemo(() => {
    return images?.[currentIndex];
  }, [images, currentIndex]);

  const handlePrevClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    prevSlide();
  }, [prevSlide]);

  const handleNextClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    nextSlide();
  }, [nextSlide]);

  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
  }, []);

  if (!images || images.length === 0) {
    return (
      <div className="gallery-card-stable" style={{ minHeight: '400px' }}>
        <div className="text-center text-gray-400 flex items-center justify-center h-full">
          Brak zdjęć do wyświetlenia dla {carType}
        </div>
      </div>
    );
  }

  return (
    <div className="gallery-card-stable">
      <div className="gallery-image-container">
        <WebPImage
          src={currentImage?.src || ''}
          srcSet={`${currentImage?.src} 1440w, ${currentImage?.src} 880w, ${currentImage?.src} 660w, ${currentImage?.src} 440w, ${currentImage?.src} 330w`}
          sizes="(max-width: 640px) 330px, (max-width: 768px) 440px, (max-width: 1024px) 660px, 880px"
          alt={currentImage?.alt || ''}
          className="gallery-image loaded"
          loading={carType === 'bmw-seria-1' ? 'eager' : 'lazy'}
          decoding="async"
          onLoad={handleImageLoad}
          onError={() => console.error(`Image failed to load: ${currentImage?.src}`)}
          width="880"
          height="880"
          fetchpriority={carType === 'bmw-seria-1' && currentIndex === 0 ? 'high' : 'auto'}
        />
        
        {images.length > 1 && (
          <>
            <button
              onClick={handlePrevClick}
              className="carousel-nav-btn carousel-nav-left"
              aria-label="Poprzednie zdjęcie"
              type="button"
            >
              <ChevronLeft className="h-4 w-4 md:h-5 md:w-5" />
            </button>
            <button
              onClick={handleNextClick}
              className="carousel-nav-right carousel-nav-btn"
              aria-label="Następne zdjęcie"
              type="button"
            >
              <ChevronRight className="h-4 w-4 md:h-5 md:w-5" />
            </button>
          </>
        )}
        
        {images.length > 1 && (
          <div className="carousel-dots">
            {images.map((_, index) => (
              <div
                key={index}
                className={`carousel-dot ${currentIndex === index ? 'active' : ''}`}
              />
            ))}
          </div>
        )}
      </div>
      
      <div className="gallery-content">
        <h3 className="gallery-title">{title}</h3>
        <div className="gallery-package">{packageDescription}</div>
        <h4 className="gallery-image-title">{currentImage?.title}</h4>
        <p className="gallery-description">{currentImage?.description}</p>
        <div className="gallery-counter">
          {currentIndex + 1} / {images.length}
        </div>
      </div>
    </div>
  );
});

PerformantCarousel.displayName = 'PerformantCarousel';

export default PerformantCarousel;